// CreateServiceOrder.tsx - Updated to follow AssetsTable pagination structure
import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { toast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { Label } from "@shared/ui/label";
import { ArrowLeft, CheckCircle } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  useCreateServiceOrder,
  useSOPList,
  useServiceOrderAssets,
  useServiceOrderDetails,
  useServiceOrderFacilities,
  useServiceOrderNetworks,
  useServiceOrderSystems,
  useUpdateServiceOrder,
} from "../../../hooks";

// Import the filter panels
import { usePriorityOptions } from "@features/ax/hooks";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { PageHeader } from "@shared/components/PageHeader";
import { Badge } from "@shared/ui/badge";
import { FIELD_CLASSES, PAGE_SIZE } from "@shared/utils/constants";
import { JobCardData, SopApiItem } from "../../../types";
import { EntityFilterPanel } from "./filters/EntityFilters";
import { SOPFilterPanel } from "./filters/SOPFilters";

const CreateServiceOrder: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEditMode = Boolean(id);
  const pageSize = PAGE_SIZE;

  // Parse URL parameters for filters and pagination - following AssetsTable pattern
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      // SOP pagination and filters
      sopPage: parseInt(searchParams.get("sopPage") || "1", 10),
      serviceAssociation: searchParams.get("serviceAssociation") || "",
      operationType: searchParams.get("operationType") || "",
      sopQuery: searchParams.get("sopQuery") || "",

      // Entity pagination and filters
      entityPage: parseInt(searchParams.get("entityPage") || "1", 10),
      area: searchParams.get("area") || "",
      type: searchParams.get("type") || "",
      entityQuery: searchParams.get("entityQuery") || "",
      system: searchParams.get("system") || "",
      facility: searchParams.get("facility") || "",
      network: searchParams.get("network") || "",
    };
  }, [location.search]);

  // State management - following AssetsTable pattern
  const [formData, setFormData] = useState({
    orderType: "",
    title: "",
    priority: "",
    selectedSOP: "",
  });

  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [editDataLoaded, setEditDataLoaded] = useState(false);

  // Search terms - following AssetsTable pattern
  const [sopSearchTerm, setSopSearchTerm] = useState(urlParams.sopQuery);
  const [entitySearchTerm, setEntitySearchTerm] = useState(
    urlParams.entityQuery
  );

  // Pagination state - following AssetsTable pattern
  const [sopCurrentPage, setSopCurrentPage] = useState(urlParams.sopPage);
  const [entityCurrentPage, setEntityCurrentPage] = useState(
    urlParams.entityPage
  );

  // Filters state - following AssetsTable pattern
  const [sopFilters, setSopFilters] = useState({
    serviceAssociation: urlParams.serviceAssociation,
    operationType: urlParams.operationType,
  });

  const [entityFilters, setEntityFilters] = useState({
    area: urlParams.area,
    type: urlParams.type,
    system: urlParams.system,
    facility: urlParams.facility,
    network: urlParams.network,
  });

  // Update state when URL changes - following AssetsTable pattern
  useEffect(() => {
    setSopCurrentPage(urlParams.sopPage);
    setEntityCurrentPage(urlParams.entityPage);
    setSopSearchTerm(urlParams.sopQuery);
    setEntitySearchTerm(urlParams.entityQuery);
    setSopFilters({
      serviceAssociation: urlParams.serviceAssociation,
      operationType: urlParams.operationType,
    });
    setEntityFilters({
      area: urlParams.area,
      type: urlParams.type,
      system: urlParams.system,
      facility: urlParams.facility,
      network: urlParams.network,
    });
  }, [urlParams]);

  // Format filters for API hooks - following AssetsTable pattern
  const formatSOPFiltersForHook = () => {
    const hookFilters: Record<string, any> = {
      page: sopCurrentPage,
      limit: pageSize,
    };

    // Add search term
    if (sopSearchTerm.trim()) {
      hookFilters.search_data = sopSearchTerm;
    }

    // Add filters
    if (sopFilters.serviceAssociation) {
      hookFilters.serviceAssociation = mapSOPServiceAssociation(
        sopFilters.serviceAssociation
      );
    }
    if (sopFilters.operationType) {
      hookFilters.operationType = sopFilters.operationType;
    }

    return hookFilters;
  };

  const formatEntityFiltersForHook = () => {
    const hookFilters: Record<string, any> = {
      page: entityCurrentPage,
      limit: pageSize,
    };

    // Add search term
    if (entitySearchTerm.trim()) {
      hookFilters.search_data = entitySearchTerm;
    }

    // Add filters
    if (entityFilters.area) {
      hookFilters.area = entityFilters.area;
    }
    if (entityFilters.type) {
      hookFilters.type = entityFilters.type;
    }
    if (entityFilters.system) {
      hookFilters.system = entityFilters.system;
    }
    if (entityFilters.facility) {
      hookFilters.facility = entityFilters.facility;
    }
    if (entityFilters.network) {
      hookFilters.network = entityFilters.network;
    }

    return hookFilters;
  };

  // Map UI values to API values for SOP - keeping the mapping logic
  const mapSOPServiceAssociation = (uiValue: string) => {
    const serviceAssociationMap: Record<string, string> = {
      assets: "2",
      systems: "4",
      facilities: "3",
      networks: "5",
    };
    return serviceAssociationMap[uiValue] || "";
  };

  // API calls - using formatted filters
  const sopHookFilters = formatSOPFiltersForHook();
  const { data: sopResponse } = useSOPList(
    sopHookFilters.search_data,
    sopHookFilters.serviceAssociation,
    sopHookFilters.operationType,
    sopHookFilters.page,
    sopHookFilters.limit
  );

  const getPriorityKey = (displayName: string): string => {
    const priority = priorityList?.find(
      (priority: any) => priority.value === displayName
    );
    return priority?.key?.toString() || "0";
  };

  const { data: priorityList } = usePriorityOptions();
  const { data: serviceOrderDetails } = useServiceOrderDetails(
    isEditMode ? id : undefined
  );

  const createServiceOrderMutation = useCreateServiceOrder();
  const updateServiceOrderMutation = useUpdateServiceOrder();

  // Get selected SOP - keeping the logic
  const selectedSOPData = useMemo(
    () =>
      sopResponse?.results?.find(
        (sop) => sop.id?.toString() === formData.selectedSOP
      ),
    [formData.selectedSOP, sopResponse?.results]
  );

  // Get service association from selected SOP
  const serviceAssociation = selectedSOPData?.sopAssociationTypeDisplay;

  // Entity API calls - using formatted filters
  const entityHookFilters = formatEntityFiltersForHook();
  const { data: assetData } = useServiceOrderAssets(
    entityHookFilters.search_data,
    entityHookFilters.type,
    entityHookFilters.system,
    entityHookFilters.facility,
    entityHookFilters.network,
    entityHookFilters.area,
    entityHookFilters.page,
    entityHookFilters.limit,
    serviceAssociation
  );

  const { data: systemData } = useServiceOrderSystems(
    entityHookFilters.search_data,
    entityHookFilters.type,
    entityHookFilters.area,
    entityHookFilters.page,
    entityHookFilters.limit,
    serviceAssociation,
    entityFilters.facility
  );

  const { data: facilityData } = useServiceOrderFacilities(
    entityHookFilters.search_data,
    entityHookFilters.type,
    entityHookFilters.area,
    entityHookFilters.page,
    entityHookFilters.limit,
    serviceAssociation
  );

  const { data: networkData } = useServiceOrderNetworks(
    entityHookFilters.search_data,
    entityHookFilters.type,
    entityHookFilters.area,
    entityHookFilters.page,
    entityHookFilters.limit,
    serviceAssociation,
    entityFilters.facility
  );

  // Entity data selection - keeping the switch logic
  const getEntityData = () => {
    if (!selectedSOPData) return null;
    const serviceAssociation = selectedSOPData.sopAssociationTypeDisplay;

    switch (serviceAssociation) {
      case "Asset":
        return assetData;
      case "System":
        return systemData;
      case "Facility":
        return facilityData;
      case "Network":
        return networkData;
      default:
        return assetData;
    }
  };

  useEffect(() => {
    if (isEditMode && serviceOrderDetails?.result && !editDataLoaded) {
      const details = serviceOrderDetails.result;
      // Set form data from service order details - using correct field names from API
      setFormData((prev) => ({
        ...prev,
        priority: details.priority?.toString(),
        selectedSOP: details.sop?.toString() || "",
        orderType: details.sopOperationTypeDisplay || "", // Fixed: use underscore version
        title: details.sopName || "", // Fixed: use underscore version
      }));

      // Set selected entities based on request_account_no (this is the correct field)
      if (details.requestAccountNo) {
        setSelectedEntities([details.requestId]);
      }

      setEditDataLoaded(true);
    }
  }, [serviceOrderDetails, isEditMode, editDataLoaded, priorityList]);

  useEffect(() => {
    if (
      isEditMode &&
      editDataLoaded &&
      formData.selectedSOP &&
      sopResponse?.results
    ) {
      // Find and auto-select the SOP
      const selectedSOP = sopResponse.results.find(
        (sop) => sop.id?.toString() === formData.selectedSOP
      );

      if (selectedSOP && serviceOrderDetails?.result) {
        // This will trigger the entity loading
        handleSOPSelect(formData.selectedSOP);
      }
    }
  }, [
    isEditMode,
    editDataLoaded,
    formData.selectedSOP,
    sopResponse,
    serviceOrderDetails,
  ]);

  // Add this effect to handle entity pre-selection once entity data is loaded
  useEffect(() => {
    if (isEditMode && editDataLoaded && serviceOrderDetails?.result) {
      const details = serviceOrderDetails.result;
      const entityData = getEntityData();

      if (entityData?.results && details.requestId) {
        const entityToSelect = entityData.results.find((entity: any) => {
          return entity.id === details.requestId;
        });

        if (entityToSelect) {
          const entityId = getEntityId(entityToSelect);
          if (!selectedEntities.includes(entityId)) {
            setSelectedEntities([entityId]);
          }
        }
      }
    }
  }, [
    assetData,
    systemData,
    facilityData,
    networkData,
    isEditMode,
    editDataLoaded,
    serviceOrderDetails,
  ]);

  // Update URL with new parameters - following AssetsTable pattern
  const updateUrl = (
    updates: Partial<{
      sopPage?: number;
      entityPage?: number;
      sopQuery?: string;
      entityQuery?: string;
      serviceAssociation?: string;
      operationType?: string;
      area?: string;
      type?: string;
      system?: string;
      facility?: string;
      network?: string;
    }>
  ) => {
    const searchParams = new URLSearchParams(location.search);

    // Update parameters
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.set(key, String(value));
      } else {
        searchParams.delete(key);
      }
    });

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  // Page change handlers - following AssetsTable pattern
  const handleSOPPageChange = (page: number) => {
    setSopCurrentPage(page);
    updateUrl({ sopPage: page });
  };

  const handleEntityPageChange = (page: number) => {
    setEntityCurrentPage(page);
    updateUrl({ entityPage: page });
  };

  // Search handlers - following AssetsTable pattern
  const handleSOPSearch = (searchValue: string) => {
    setSopSearchTerm(searchValue);
    updateUrl({ sopQuery: searchValue, sopPage: 1 });
    setSopCurrentPage(1);
  };

  const handleEntitySearch = (searchValue: string) => {
    setEntitySearchTerm(searchValue);
    updateUrl({ entityQuery: searchValue, entityPage: 1 });
    setEntityCurrentPage(1);
  };

  // Filter handlers - following AssetsTable pattern
  const handleSOPApplyFilters = useCallback((newFilters: any) => {
    setSopFilters(newFilters);
    updateUrl({
      ...newFilters,
      sopPage: 1,
    });
    setSopCurrentPage(1);
  }, []);

  const handleSOPResetFilters = useCallback(() => {
    setSopFilters({
      serviceAssociation: "",
      operationType: "",
    });
    setSopSearchTerm("");
    setSopCurrentPage(1);
    updateUrl({
      serviceAssociation: "",
      operationType: "",
      sopQuery: "",
      sopPage: 1,
    });
  }, []);

  const handleEntityApplyFilters = useCallback((newFilters: any) => {
    setEntityFilters(newFilters);
    updateUrl({
      ...newFilters,
      entityPage: 1,
    });
    setEntityCurrentPage(1);
  }, []);

  const handleEntityResetFilters = useCallback(() => {
    setEntityFilters({
      area: "",
      type: "",
      system: "",
      facility: "",
      network: "",
    });
    setEntitySearchTerm("");
    setEntityCurrentPage(1);
    updateUrl({
      area: "",
      type: "",
      system: "",
      facility: "",
      network: "",
      entityQuery: "",
      entityPage: 1,
    });
  }, []);

  // Selection handlers - keeping CreateServiceOrder logic
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  }, []);

  const handleSOPSelect = useCallback(
    (sopId: string) => {
      const sop = sopResponse?.results?.find((s) => s.id?.toString() === sopId);
      if (sop) {
        const updates: any = { selectedSOP: sopId };

        if (!formData.orderType) updates.orderType = sop.sopType;
        if (!formData.title) updates.title = sop.name;
        if (!formData.priority) updates.priority = sop.priorityDisplay;

        setFormData((prev) => ({ ...prev, ...updates }));
        setSelectedEntities([]);
        handleEntityResetFilters();
      }
    },
    [formData, sopResponse?.results]
  );

  // Entity selection handlers - keeping the logic
  const handleEntitySelect = (entityId: string) => {
    setSelectedEntities((prev) =>
      prev.includes(entityId)
        ? prev.filter((id) => id !== entityId)
        : [...prev, entityId]
    );
  };

  // Helper to get entity ID based on type
  const getEntityId = (entity: any) => {
    if (!selectedSOPData) return entity.id;
    const serviceAssociation = selectedSOPData.sopAssociationTypeDisplay;

    if (serviceAssociation?.includes("Asset")) return entity.assetNo;
    if (serviceAssociation?.includes("System")) return entity.systemNo;
    if (serviceAssociation?.includes("Facility")) return entity.facilityNo;
    if (serviceAssociation?.includes("Network")) return entity.networkNo;

    return entity.id;
  };

  // Get row key based on entity type
  const getRowKey = () => {
    if (!selectedSOPData) return "id";
    const serviceAssociation = selectedSOPData.sopAssociationTypeDisplay;

    if (serviceAssociation?.includes("Asset")) return "assetNo";
    if (serviceAssociation?.includes("System")) return "systemNo";
    if (serviceAssociation?.includes("Facility")) return "facilityNo";
    if (serviceAssociation?.includes("Network")) return "networkNo";

    return "id";
  };

  const step1Complete = Boolean(formData.selectedSOP);

  const priorityOptions = useMemo(() => {
    return (
      priorityList?.map((assetType: any) => ({
        value: assetType.key,
        label: assetType.value,
      })) || []
    );
  }, [priorityList]);

  // Form field definitions
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "priority",
        type: "select",
        label: "Priority",
        placeholder: "Select priority level",
        required: true,
        options: priorityOptions,
        className: "col-span-1 mt-2",
        showHelperTooltip: true,
        tooltipText:
          "Indicates the urgency level of the service order (e.g., Low, Medium, High).",
        tooltipPosition: "right",
      },
    ],
    [priorityOptions]
  );

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    if (formData.priority) {
      form.setValue("priority", formData.priority);
    }
  }, [formData.priority, form]);

  type BadgeVariant = "default" | "success" | "warning" | "destructive";

  function getConditionBadge(score?: number): {
    label: string;
    variant: BadgeVariant;
  } {
    if (score === null || score === undefined) {
      return { label: "NA", variant: "default" };
    }

    switch (true) {
      case score > 80:
        return { label: "Good", variant: "success" };
      case score >= 40 && score <= 80:
        return { label: "Fair", variant: "warning" };
      case score < 40:
        return { label: "Poor", variant: "destructive" };
      default:
        return { label: "NA", variant: "default" };
    }
  }

  // SOP Table Columns
  const sopColumns: TableColumn<SopApiItem>[] = useMemo(
    () => [
      {
        key: "select",
        header: "Select",
        width: "w-16",
        type: "custom",
        sortable: false,
        render: (_: any, row: SopApiItem) => (
          <input
            type="radio"
            name="selectedSOP"
            checked={formData.selectedSOP === row.id?.toString()}
            onChange={() => handleSOPSelect(row.id?.toString() || "")}
            className="h-4 w-4 text-blue-600"
          />
        ),
        headerTooltip:
          "Choose the Standard Operating Procedure (SOP) to apply to this service order. Only one SOP can be selected per order.",
      },
      {
        key: "serviceNo",
        header: "SOP ID",
        type: "custom",
        render: (value: string) => <span className="font-medium">{value}</span>,
        headerTooltip:
          "The unique identifier for this Standard Operating Procedure. This ID is used for tracking and reference purposes.",
      },
      {
        key: "name",
        header: "SOP Name",
        type: "custom",
        headerTooltip:
          "The descriptive name of the Standard Operating Procedure that explains its purpose and scope.",
      },
      {
        key: "sopAssociationTypeDisplay",
        header: "Service Association",
        type: "badge",
        badgeConfig: {
          getClassName: (value: string) => {
            if (value?.includes("Asset")) return "bg-blue-100 text-blue-800";
            if (value?.includes("System")) return "bg-green-100 text-green-800";
            if (value?.includes("Facility"))
              return "bg-purple-100 text-purple-800";
            if (value?.includes("Network"))
              return "bg-orange-100 text-orange-800";
            return "bg-gray-100 text-gray-800";
          },
        },
        headerTooltip:
          "The type of entity this SOP is designed to work with (Asset, System, Facility, or Network). This determines what can be scheduled with this SOP.",
      },
      {
        key: "operationTypeDisplay",
        header: "Operation Type",
        type: "badge",
        badgeConfig: {
          getClassName: () => "bg-purple-100 text-purple-800",
        },
        headerTooltip:
          "The category of work or maintenance operation that this SOP covers (e.g., Preventive Maintenance, Repair, Inspection, Emergency Response).",
      },
      {
        key: "taskCount",
        header: "Tasks",
        type: "custom",
        render: (value: number) => `${value} tasks`,
        headerTooltip:
          "The total number of individual tasks or steps included in this Standard Operating Procedure.",
      },
      {
        key: "estTotalCost",
        header: "Total Cost",
        type: "custom",
        render: (value: string) => (
          <span className="font-medium text-green-600">{value ?? "NA"}</span>
        ),
        headerTooltip:
          "The estimated total cost to complete all tasks in this SOP, including labor, materials, and equipment.",
      },
    ],
    [formData.selectedSOP, handleSOPSelect]
  );

  // Dynamic Entity Columns - keeping comprehensive definitions
  const getEntityColumns = useCallback((): TableColumn<any>[] => {
    if (!selectedSOPData) return [];

    const serviceAssociation = selectedSOPData.sopAssociationTypeDisplay;

    if (serviceAssociation?.includes("Asset")) {
      return [
        {
          key: "assetNo",
          header: "Asset ID",
          type: "custom",
          render: (value: string) => (
            <span className="font-medium">{value}</span>
          ),
          headerTooltip:
            "The unique identifier for this asset. Click to view its detailed profile.",
        },
        {
          key: "assetName",
          header: "Asset Name",
          type: "custom",
          headerTooltip: "The common name used to identify this asset.",
        },
        {
          key: "assetTypeDisplay",
          header: "Type",
          type: "custom",
          headerTooltip: "The general category this asset belongs to.",
        },
        {
          key: "conditionScores",
          header: "Condition",
          type: "badge",
          headerTooltip: "The specific condition of the asset.",
          render: (value: number) => {
            const { label, variant } = getConditionBadge(value);
            return <Badge variant={variant}>{label}</Badge>;
          },
        },
        {
          key: "facilityName",
          header: "Facility",
          type: "custom",
          headerTooltip:
            "The main facility where this asset is located or managed from.",
        },
        {
          key: "systemNetwork",
          header: "System/Network",
          type: "text",
          render: (value: number, row: any) => (
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {row.systemName ?? row.networkName ?? "NA"}
              </span>
            </div>
          ),
          headerTooltip:
            "The parent system or network this asset is a component of.",
        },
        {
          key: "location",
          header: "Location",
          type: "custom",
          render: (value) => <HoverRevealText text={value} />,
          headerTooltip: "The physical location or address of this asset.",
        },
      ];
    } else if (serviceAssociation?.includes("System")) {
      return [
        {
          key: "systemNo",
          header: "System ID",
          type: "custom",
          render: (value: string) => (
            <span className="font-medium">{value}</span>
          ),
          headerTooltip:
            "The unique identifier for this system. Click to view the detailed system profile.",
        },
        {
          key: "systemName",
          header: "System Name",
          type: "custom",
          headerTooltip: "The common name used to identify this system.",
        },
        {
          key: "systemTypeDisplay",
          header: "Type",
          type: "custom",
          headerTooltip:
            "The current real-time operational status of the system.",
        },
        {
          key: "criticalityLevelDisplay",
          header: "Criticality",
          type: "badge",
          headerTooltip:
            "An assessment of the system's importance. Higher criticality means its failure has a greater impact.",
        },
        {
          key: "assetCount",
          header: "Assets",
          type: "custom",
          headerTooltip: "The total number of assets in the system.",
        },
        {
          key: "facilityName",
          header: "Facility",
          type: "custom",
          headerTooltip:
            "The main facility where this system is located or managed from.",
        },
        {
          key: "location",
          header: "Location",
          type: "custom",
          headerTooltip:
            "The specific physical location or zone of the system.",
          render: (value) => (
            <span
              title={value}
              className="inline-block max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200 px-1 py-1 rounded"
            >
              {value?.length > 15
                ? `${value.substring(0, 15)}...`
                : value || "N/A"}
            </span>
          ),
        },
      ];
    } else if (serviceAssociation?.includes("Facility")) {
      return [
        {
          key: "facilityNo",
          header: "Facility ID",
          type: "custom",
          render: (value: string) => (
            <span className="font-medium">{value}</span>
          ),
          headerTooltip:
            "The unique identifier for the facility. Click to view the facility's detailed profile.",
        },
        {
          key: "facilityName",
          header: "Name",
          type: "custom",
          headerTooltip: "The common name of the facility.",
        },
        {
          key: "facilityTypeDisplay",
          header: "Type",
          type: "custom",
          headerTooltip:
            "The primary function or classification of the facility.",
        },
        {
          key: "conditionScore",
          header: "Condition Score",
          type: "badge",
          render: (value: number) => {
            const { label, variant } = getConditionBadge(value);
            return <Badge variant={variant}>{label}</Badge>;
          },
        },
        {
          key: "systemsCount",
          header: "Systems",
          type: "custom",
          headerTooltip: "The total number of distinct operational systems.",
        },
        {
          key: "assetsCount",
          header: "Assets",
          type: "custom",
          headerTooltip: "The total number of individual assets registered.",
        },
        {
          key: "addressSiteLabel",
          header: "Location",
          type: "custom",
          headerTooltip:
            "The specific physical location or zone of the facility.",
          render: (value) => (
            <span
              title={value}
              className="inline-block max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200 px-1 py-1 rounded"
            >
              {value?.length > 15
                ? `${value.substring(0, 15)}...`
                : value || "N/A"}
            </span>
          ),
        },
      ];
    } else if (serviceAssociation?.includes("Network")) {
      return [
        {
          key: "networkNo",
          header: "Network ID",
          type: "custom",
          render: (value: string) => (
            <span className="font-medium">{value}</span>
          ),
          headerTooltip:
            "The unique identifier for this network. Click to view its detailed profile.",
        },
        {
          key: "networkName",
          header: "Network Name",
          type: "custom",
          headerTooltip: "The common name used to identify this network.",
        },
        {
          key: "networkType",
          header: "Type",
          type: "custom",
          headerTooltip:
            "The classification of this network (e.g., Distribution, Transmission, Service).",
        },
        {
          key: "conditionScore",
          header: "Condition Score",
          type: "badge",
          render: (value: number) => {
            const { label, variant } = getConditionBadge(value);
            return (
              <div className="flex items-center gap-2">
                <span className="font-medium">{value}</span>
                <Badge variant={variant}>{label}</Badge>
              </div>
            );
          },
        },
        {
          key: "assetCount",
          header: "Assets",
          type: "custom",
          headerTooltip: "The total number of individual assets registered.",
        },
        {
          key: "zonesDisplay",
          header: "Zones/Areas",
          type: "custom",
          render: (value: any) =>
            Array.isArray(value) && value?.length !== 0
              ? value.join(", ")
              : "NA",
          headerTooltip:
            "The geographic zones and areas that receive water from this network.",
        },
      ];
    }

    return [];
  }, [selectedSOPData, selectedEntities, handleEntitySelect, getEntityId]);

  const getEntityTypeName = useCallback(() => {
    if (!selectedSOPData) return "Entity";
    const serviceAssociation = selectedSOPData.sopAssociationTypeDisplay;
    return serviceAssociation;
  }, [selectedSOPData]);

  const handleFinalSubmit = useCallback(async () => {
    if (!selectedSOPData || selectedEntities.length === 0) {
      return;
    }

    // Get raw entity data
    const rawEntityData = getEntityData()?.results || [];

    // Helper functions to get the correct property names based on entity type
    const getEntityName = (entity: any) => {
      const serviceAssociation = selectedSOPData.sopAssociationTypeDisplay;

      if (serviceAssociation?.includes("Asset")) return entity?.assetName;
      if (serviceAssociation?.includes("System")) return entity?.systemName;
      if (serviceAssociation?.includes("Facility")) return entity?.facilityName;
      if (serviceAssociation?.includes("Network")) return entity?.networkName;
    };

    const getEntityLocation = (entity: any) => {
      const serviceAssociation = selectedSOPData.sopAssociationTypeDisplay;
      if (serviceAssociation?.includes("Asset")) return entity?.location || "";
      if (serviceAssociation?.includes("System")) return entity?.location || "";
      if (serviceAssociation?.includes("Facility"))
        return entity?.location || "";
      if (serviceAssociation?.includes("Network"))
        return entity?.location || "";
      return "";
    };

    // Transform your selected entities to match API format
    const jobCardData: JobCardData[] = selectedEntities.map((entityId) => {
      const entity = rawEntityData.find((e: any) => {
        // Check the appropriate ID field based on service association
        const serviceAssociation = selectedSOPData.sopAssociationTypeDisplay;
        if (serviceAssociation?.includes("Asset"))
          return e.assetNo === entityId;
        if (serviceAssociation?.includes("System"))
          return e.systemNo === entityId;
        if (serviceAssociation?.includes("Facility"))
          return e.facilityNo === entityId;
        if (serviceAssociation?.includes("Network"))
          return e.networkNo === entityId;
        return false;
      });

      return {
        sop: parseInt(selectedSOPData.id?.toString() || "0"),
        requestId: entity?.id,
        requestUsername: getEntityName(entity),
        requestAccountNo: entityId,
        consumerAddress: getEntityLocation(entity),
        priority: formData.priority,
      };
    });

    try {
      if (isEditMode && id) {
        // Pass both id and jobCardData as an object to match the hook signature
        await updateServiceOrderMutation.mutateAsync({
          id: parseInt(id),
          jobCardData: jobCardData,
        });
      } else {
        await createServiceOrderMutation.mutateAsync(jobCardData);
      }
      navigate("/ax/service-order/landing?page=1&tab=management");
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
      });
    }
  }, [
    selectedSOPData,
    formData,
    selectedEntities,
    getEntityData,
    createServiceOrderMutation,
    updateServiceOrderMutation,
    navigate,
    isEditMode,
    id,
  ]);

  const handleCancel = useCallback(() => {
    navigate("/ax/service-order/landing?page=1&tab=management");
  }, [navigate]);

  // Calculate pagination info - following AssetsTable pattern
  const sopTotalItems = sopResponse?.count || 0;
  const sopTotalPages = Math.ceil(sopTotalItems / pageSize);
  const sopStartItem = (sopCurrentPage - 1) * pageSize + 1;
  const sopEndItem = Math.min(sopCurrentPage * pageSize, sopTotalItems);

  const entityData = getEntityData();
  const entityTotalItems = entityData?.count || 0;
  const entityTotalPages = Math.ceil(entityTotalItems / pageSize);
  const entityStartItem = (entityCurrentPage - 1) * pageSize + 1;
  const entityEndItem = Math.min(
    entityCurrentPage * pageSize,
    entityTotalItems
  );

  // Check if there are active filters - following AssetsTable pattern
  const hasActiveSOPFilters =
    sopSearchTerm || sopFilters.serviceAssociation || sopFilters.operationType;

  const hasActiveEntityFilters =
    entitySearchTerm ||
    entityFilters.area ||
    entityFilters.type ||
    entityFilters.system ||
    entityFilters.facility ||
    entityFilters.network;

  // Initialize data loaded state
  useEffect(() => {
    setIsDataLoaded(true);
  }, []);

  return (
    <div className="mx-auto px-4 space-y-6">
      <div className="mb-4 flex items-center gap-4">
        {/* Back Arrow */}
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700"
          onClick={() => navigate("/ax/service-order/landing/")}
        />

        {/* Page Header */}
        <div className="flex flex-col">
          <PageHeader
            title={isEditMode ? "Edit Service Order" : "Create Service Order"}
            subtitle={
              isEditMode ? "Edit Service Order" : "Create Service Order"
            }
          />
        </div>
      </div>

      <div className="space-y-8">
        {/* Step 1: Define the Service Order Details */}
        <Card>
          <CardHeader>
            <PageHeader
              title="Describe the Work"
              subtitle="Define the type, scope, and priority of the work to be performed"
            />
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Service Order Details Form */}
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={() => {}}
              onFieldChange={handleFieldChange}
              gridColumns={3}
              responsiveColumns={{
                sm: 1,
                md: 3,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                spacing: "comfortable",
                classes: FIELD_CLASSES,
              }}
            />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">
                  Standard Operating Procedures (SOPs) Selection
                </Label>
                <p className="text-sm text-gray-600">
                  Select the standard operating procedure that will be applied
                  to this schedule
                </p>
              </div>

              <GenericTable
                data={sopResponse?.results || []}
                columns={sopColumns}
                rowKey="id"
                searchPlaceholder="Search by SOP ID, SOP Name..."
                searchParamName="sopQuery"
                searchDebounceMs={300}
                searchValue={sopSearchTerm}
                onSearch={handleSOPSearch}
                filterPanel={
                  <SOPFilterPanel
                    currentFilters={sopFilters}
                    onApplyFilters={handleSOPApplyFilters}
                    onResetFilters={handleSOPResetFilters}
                  />
                }
                pagination={{
                  currentPage: sopCurrentPage,
                  totalPages: sopTotalPages,
                  totalItems: sopTotalItems,
                  itemsPerPage: pageSize,
                  startItem: sopTotalItems > 0 ? sopStartItem : 0,
                  endItem: sopTotalItems > 0 ? sopEndItem : 0,
                }}
                onPageChange={handleSOPPageChange}
                emptyMessage="No SOPs found matching your criteria"
                getRowClassName={(row) =>
                  formData.selectedSOP === row.id?.toString()
                    ? "bg-blue-50"
                    : ""
                }
                filters={
                  hasActiveSOPFilters ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Filters active
                      </span>
                    </div>
                  ) : null
                }
              />

              {formData.selectedSOP && selectedSOPData && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-800">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">
                      Selected SOP: {selectedSOPData.name} (
                      {selectedSOPData.serviceNo})
                    </span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Select Entities */}
        {step1Complete && (
          <Card>
            <CardHeader>
              <PageHeader
                title={`${getEntityTypeName()}(s) to be Scheduled`}
                subtitle={`Select the ${getEntityTypeName()} that will be part of this schedule using the table below`}
              />
            </CardHeader>
            <CardContent className="space-y-4">
              <GenericTable
                data={entityData?.results || []}
                columns={getEntityColumns()}
                rowKey={getRowKey()}
                searchPlaceholder={`Search by ID, Name...`}
                searchParamName="entityQuery"
                searchDebounceMs={300}
                searchValue={entitySearchTerm}
                selectable={true}
                selectedRows={selectedEntities}
                onSelectionChange={setSelectedEntities}
                onSearch={handleEntitySearch}
                filterPanel={
                  <EntityFilterPanel
                    currentFilters={entityFilters}
                    onApplyFilters={handleEntityApplyFilters}
                    onResetFilters={handleEntityResetFilters}
                    serviceAssociation={
                      selectedSOPData?.sopAssociationTypeDisplay || ""
                    }
                  />
                }
                pagination={{
                  currentPage: entityCurrentPage,
                  totalPages: entityTotalPages,
                  totalItems: entityTotalItems,
                  itemsPerPage: pageSize,
                  startItem: entityTotalItems > 0 ? entityStartItem : 0,
                  endItem: entityTotalItems > 0 ? entityEndItem : 0,
                }}
                onPageChange={handleEntityPageChange}
                emptyMessage={`No ${getEntityTypeName()} found`}
                filters={
                  hasActiveEntityFilters ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Filters active
                      </span>
                    </div>
                  ) : null
                }
              />

              {/* Selection Summary */}
              {selectedEntities.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-800">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">
                      You have selected {selectedEntities.length}{" "}
                      {getEntityTypeName()}
                      {selectedEntities.length !== 1 ? "s" : ""}.
                      {isEditMode
                        ? " The service order will be updated with these entities."
                        : " A service order will be created for each."}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t">
        <Button variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleFinalSubmit}
          disabled={
            !step1Complete ||
            selectedEntities.length === 0 ||
            createServiceOrderMutation.isPending ||
            updateServiceOrderMutation.isPending
          }
          className="min-w-40"
        >
          {createServiceOrderMutation.isPending ||
          updateServiceOrderMutation.isPending ? (
            <>{isEditMode ? "Updating..." : "Creating..."}</>
          ) : (
            <>
              {isEditMode ? "Update" : "Create"}
              {selectedEntities.length > 0
                ? ` ${selectedEntities.length}`
                : ""}{" "}
              Service Order{selectedEntities.length !== 1 ? "s" : ""}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default CreateServiceOrder;
